import { callUIAPI } from '../../../utils/apiUtils';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import { replayRest } from '../replay';
import type { RunRecovery } from '../components/FirstActionFlow/errors';

export interface OnboardingRunStep {
  stepKey: string;
  lane: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  weight: number;
  messageKey: string | null;
  messageParams: Record<string, string | number> | null;
  doneMessageKey: string | null;
  output: Record<string, unknown> | null;
  error: { message: string; code: string | null; retryable: boolean } | null;
}

type OnboardingRunLaneStep = Pick<OnboardingRunStep, 'lane' | 'status' | 'weight'>;

export interface OnboardingRun {
  id: number;
  kind: 'onboarding_bootstrap' | 'site_health';
  scanResultId: number;
  status: 'queued' | 'running' | 'pausing' | 'paused' | 'cancelling' | 'cancelled' | 'completed' | 'failed';
  pct: number;
  steps?: OnboardingRunLaneStep[];
  resultPayload: Record<string, unknown> | null;
  error: { message: string; code: string | null; errorCode?: string; retryable: boolean; restartAllowed?: boolean; recovery?: RunRecovery; detail?: string } | null;
}

export interface OnboardingRunSnapshot {
  run: OnboardingRun;
  steps: OnboardingRunStep[];
  lanes: Record<string, number | null>;
  cursor: string;
}

export async function startBootstrapRun(input: { scanResultId: number; companyTypeId?: number; desc?: string; restart?: boolean; deferUntilScanComplete?: boolean }): Promise<OnboardingRun> {
  return replayRest('onboarding-runs:create', async () => {
    const res = await callUIAPI('onboarding-runs', input, apiConstants.METHOD_POST, true);
    return res.data as OnboardingRun;
  });
}

export async function getActiveRuns(): Promise<OnboardingRun[]> {
  const res = await callUIAPI('onboarding-runs/active', undefined, apiConstants.METHOD_GET, true);
  return res.data;
}

export async function getLatestBootstrapRun(scanResultId: number): Promise<OnboardingRun | null> {
  const res = await callUIAPI(`onboarding-runs/latest?scanResultId=${scanResultId}`, undefined, apiConstants.METHOD_GET, true);
  return res.data ?? null;
}

export async function getRunSnapshot(runId: number): Promise<OnboardingRunSnapshot> {
  return replayRest('onboarding-runs:snapshot', async () => {
    const res = await callUIAPI(`onboarding-runs/${runId}`, undefined, apiConstants.METHOD_GET, true);
    return res.data as OnboardingRunSnapshot;
  });
}

export async function retryRun(runId: number): Promise<OnboardingRun> {
  return (await callUIAPI(`onboarding-runs/${runId}/retry`, {}, apiConstants.METHOD_POST, true)).data;
}

export async function cancelRun(runId: number): Promise<OnboardingRun> {
  return (await callUIAPI(`onboarding-runs/${runId}/cancel`, {}, apiConstants.METHOD_POST, true)).data;
}

export function runStreamUrl(runId: number, cursor?: string): string {
  const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return `${SERVER_URL}/api/ui/onboarding-runs/${runId}/stream${params}`;
}
